import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  UserIcon,
  EnvelopeIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  DocumentTextIcon,
  GiftIcon
} from '@heroicons/react/24/outline';

const BottomNav = () => {
  const location = useLocation();
  const path = location.pathname;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navItems = [
    { name: 'Dashboard', path: '/', icon: HomeIcon, shortName: 'Home' },
    { name: 'Plans', path: '/plans', icon: ChartBarIcon, shortName: 'Plans' },
    { name: 'Deposit', path: '/deposit', icon: ArrowDownTrayIcon, shortName: 'Deposit' },
    { name: 'Withdraw', path: '/withdraw', icon: ArrowUpTrayIcon, shortName: 'Withdraw' },
    { name: 'Profile', path: '/profile', icon: UserIcon, shortName: 'Profile' },
    { name: 'My Plans', path: '/myplans', icon: ClockIcon, shortName: 'Plans' },
    { name: 'History', path: '/history', icon: DocumentTextIcon, shortName: 'History' },
    { name: 'Referral', path: '/referral', icon: GiftIcon, shortName: 'Referral' },
    { name: 'Contact', path: '/contact', icon: EnvelopeIcon, shortName: 'Support' },
  ];

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const toggleDesktopCollapse = () => setIsDesktopCollapsed(!isDesktopCollapsed);

  // Mobile Navigation - Dark Theme
  if (isMobile) {
    return (
      <>
        <nav style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          maxWidth: '480px',
          margin: '0 auto',
          background: 'rgba(26, 26, 46, 0.95)',
          backdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(0, 212, 255, 0.3)',
          zIndex: 50,
          boxShadow: '0 -4px 20px rgba(0, 212, 255, 0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            height: '64px',
            padding: '0 8px'
          }}>
            {navItems.slice(0, 4).map((item) => {
              const Icon = item.icon;
              const isActive = path === item.path;
              
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    padding: '8px 0',
                    borderRadius: '12px',
                    transition: 'all 0.2s',
                    textDecoration: 'none',
                    color: isActive ? '#00d4ff' : '#94a3b8',
                    position: 'relative'
                  }}
                >
                  <Icon style={{
                    width: '20px',
                    height: '20px',
                    transition: 'transform 0.2s',
                    transform: isActive ? 'scale(1.1)' : 'scale(1)'
                  }} />
                  <span style={{
                    fontSize: '11px',
                    marginTop: '4px',
                    fontWeight: '500'
                  }}>{item.shortName}</span>
                  {isActive && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      width: '24px',
                      height: '3px',
                      background: '#00d4ff',
                      borderRadius: '3px',
                      boxShadow: '0 0 8px #00d4ff'
                    }} />
                  )}
                </Link>
              );
            })}
            
            <button
              onClick={toggleMobileMenu}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                padding: '8px 0',
                borderRadius: '12px',
                transition: 'all 0.2s',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: isMobileMenuOpen ? '#00d4ff' : '#94a3b8'
              }}
            >
              {isMobileMenuOpen ? (
                <XMarkIcon style={{ width: '20px', height: '20px' }} />
              ) : (
                <Bars3Icon style={{ width: '20px', height: '20px' }} />
              )}
              <span style={{
                fontSize: '11px',
                marginTop: '4px',
                fontWeight: '500'
              }}>More</span>
            </button>
          </div>

          {/* Mobile Dropdown Menu - Dark Theme */}
          {isMobileMenuOpen && (
            <div style={{
              position: 'absolute',
              bottom: '64px',
              left: 0,
              right: 0,
              background: 'rgba(26, 26, 46, 0.98)',
              backdropFilter: 'blur(20px)',
              borderTop: '1px solid rgba(0, 212, 255, 0.3)',
              borderBottom: '1px solid rgba(0, 212, 255, 0.3)',
              boxShadow: '0 -10px 30px rgba(0, 212, 255, 0.1)',
              borderRadius: '16px 16px 0 0',
              maxHeight: '70vh',
              overflowY: 'auto',
              animation: 'slideUp 0.3s ease-out'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px',
                borderBottom: '1px solid rgba(0, 212, 255, 0.2)'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#00d4ff',
                  margin: 0
                }}>More Options</h3>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  style={{
                    padding: '4px',
                    borderRadius: '8px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#94a3b8'
                  }}
                >
                  <XMarkIcon style={{ width: '20px', height: '20px' }} />
                </button>
              </div>
              <div style={{ padding: '8px' }}>
                {navItems.slice(4).map((item) => {
                  const Icon = item.icon;
                  const isActive = path === item.path;
                  
                  return (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        borderRadius: '12px',
                        transition: 'all 0.2s',
                        textDecoration: 'none',
                        background: isActive ? 'rgba(0, 212, 255, 0.15)' : 'transparent',
                        color: isActive ? '#00d4ff' : '#cbd5e1',
                        marginBottom: '4px'
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.background = 'rgba(0, 212, 255, 0.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                    >
                      <Icon style={{ width: '20px', height: '20px' }} />
                      <span style={{ flex: 1, fontSize: '14px', fontWeight: '500' }}>{item.name}</span>
                      {isActive && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          background: '#00d4ff',
                          borderRadius: '50%',
                          boxShadow: '0 0 8px #00d4ff'
                        }} />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </nav>
        
        {/* Spacer for main content */}
        <div style={{ paddingBottom: '64px' }} />
      </>
    );
  }

  // Tablet Navigation - Dark Theme (Horizontal Scroll)
  if (isTablet) {
    return (
      <>
        <nav style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'rgba(26, 26, 46, 0.95)',
          backdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(0, 212, 255, 0.3)',
          zIndex: 50,
          boxShadow: '0 -4px 20px rgba(0, 212, 255, 0.1)'
        }}>
          <div style={{
            maxWidth: '768px',
            margin: '0 auto',
            padding: '0 8px'
          }}>
            <div style={{
              display: 'flex',
              overflowX: 'auto',
              gap: '4px',
              padding: '8px 0',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = path === item.path;
                
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      minWidth: '70px',
                      padding: '8px 12px',
                      borderRadius: '12px',
                      transition: 'all 0.2s',
                      textDecoration: 'none',
                      color: isActive ? '#00d4ff' : '#94a3b8',
                      position: 'relative'
                    }}
                  >
                    <Icon style={{
                      width: '20px',
                      height: '20px',
                      transition: 'transform 0.2s',
                      transform: isActive ? 'scale(1.1)' : 'scale(1)'
                    }} />
                    <span style={{
                      fontSize: '11px',
                      marginTop: '4px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap'
                    }}>{item.shortName}</span>
                    {isActive && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        width: '32px',
                        height: '3px',
                        background: '#00d4ff',
                        borderRadius: '3px',
                        boxShadow: '0 0 8px #00d4ff'
                      }} />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>
        <style>{`
          .tablet-nav-scroll::-webkit-scrollbar {
            display: none;
          }
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
        <div style={{ paddingBottom: '72px' }} />
      </>
    );
  }

  // Desktop Side Navigation - Dark Theme
  return (
    <>
      <nav style={{
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        width: isDesktopCollapsed ? '80px' : '256px',
        background: 'rgba(26, 26, 46, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRight: '1px solid rgba(0, 212, 255, 0.3)',
        zIndex: 50,
        transition: 'width 0.3s ease',
        boxShadow: '2px 0 20px rgba(0, 212, 255, 0.1)'
      }}>
        <div style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isDesktopCollapsed ? 'center' : 'space-between',
            padding: '16px',
            borderBottom: '1px solid rgba(0, 212, 255, 0.2)'
          }}>
            {!isDesktopCollapsed && (
              <h3 style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#00d4ff',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                margin: 0
              }}>
                Navigation
              </h3>
            )}
            <button
              onClick={toggleDesktopCollapse}
              style={{
                padding: '8px',
                borderRadius: '8px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#94a3b8',
                transition: 'all 0.2s',
                margin: isDesktopCollapsed ? '0 auto' : 0
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
            >
              {isDesktopCollapsed ? (
                <ChevronDownIcon style={{ width: '16px', height: '16px' }} />
              ) : (
                <ChevronUpIcon style={{ width: '16px', height: '16px' }} />
              )}
            </button>
          </div>

          {/* Navigation Items */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: isDesktopCollapsed ? '8px' : '12px'
          }}>
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = path === item.path;
              
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '12px',
                    transition: 'all 0.2s',
                    marginBottom: '4px',
                    textDecoration: 'none',
                    background: isActive ? 'rgba(0, 212, 255, 0.15)' : 'transparent',
                    color: isActive ? '#00d4ff' : '#cbd5e1',
                    justifyContent: isDesktopCollapsed ? 'center' : 'flex-start',
                    padding: isDesktopCollapsed ? '12px' : '12px 16px',
                    position: 'relative'
                  }}
                  title={isDesktopCollapsed ? item.name : undefined}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(0, 212, 255, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <Icon style={{
                    width: '20px',
                    height: '20px',
                    transition: 'transform 0.2s',
                    transform: isActive ? 'scale(1.1)' : 'scale(1)'
                  }} />
                  {!isDesktopCollapsed && (
                    <>
                      <span style={{
                        flex: 1,
                        fontSize: '14px',
                        fontWeight: '500',
                        marginLeft: '12px'
                      }}>{item.name}</span>
                      {isActive && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          background: '#00d4ff',
                          borderRadius: '50%',
                          boxShadow: '0 0 8px #00d4ff'
                        }} />
                      )}
                    </>
                  )}
                  {isActive && isDesktopCollapsed && (
                    <div style={{
                      position: 'absolute',
                      right: 0,
                      width: '3px',
                      height: '20px',
                      background: '#00d4ff',
                      borderRadius: '3px',
                      boxShadow: '0 0 8px #00d4ff'
                    }} />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Footer */}
          {!isDesktopCollapsed && (
            <div style={{
              padding: '16px',
              borderTop: '1px solid rgba(0, 212, 255, 0.2)',
              textAlign: 'center'
            }}>
              <p style={{
                fontSize: '11px',
                color: '#64748b',
                margin: 0
              }}>
                {navItems.length} menu items
              </p>
            </div>
          )}
        </div>
      </nav>

      {/* Spacer for main content */}
      <div style={{
        transition: 'margin-left 0.3s ease',
        marginLeft: isDesktopCollapsed ? '80px' : '256px'
      }} />
      
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .tablet-nav-scroll::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </>
  );
};

export default BottomNav;